<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue';
import { Primitive, type PrimitiveProps } from 'reka-ui';
import { type ButtonVariants, buttonVariants } from '.';
import { cn } from '../../../lib/utils';

interface Props extends PrimitiveProps {
  variant?: ButtonVariants['variant'];
  size?: ButtonVariants['size'];
  class?: HTMLAttributes['class'];
  loading?: boolean;
  to?: string | object;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
  loading: false,
  to: undefined,
  variant: 'default',
  size: 'default',
  disabled: false,
  class: '',
});

const isRouterLink = computed(() => !!props.to);
</script>

<template>
  <div class="button-wrapper">
    <RouterLink
      v-if="isRouterLink"
      :to="props.to || ''"
      :class="
        cn(buttonVariants({ variant, size }), 'pointer-events-auto', props.class, {
          'opacity-50 pointer-events-none': loading,
        })
      "
    >
      <slot />
    </RouterLink>

    <Primitive
      v-else
      :as="as"
      :type="as === 'button' ? props.type : undefined"
      :disabled="props.disabled || props.loading"
      :class="
        cn(buttonVariants({ variant, size }), 'pointer-events-auto', props.class, {
          'opacity-50 pointer-events-none': loading || disabled,
        })
      "
    >
      <div v-if="props.loading" class="dot-wave">
        <span />
        <span />
        <span />
      </div>
      <slot v-else />
    </Primitive>
  </div>
</template>

<style scoped>
.button-wrapper {
  pointer-events: none;
}

.dot-wave {
  display: flex;
  align-items: center;
  justify-content: center;
}

.dot-wave span {
  display: inline-block;
  width: 0.4rem;
  height: 0.4rem;
  margin: 0 0.2rem;
  background-color: currentColor;
  border-radius: 50%;
  animation: wave 1.2s infinite ease-in-out;
}

.dot-wave span:nth-child(1) {
  animation-delay: -0.4s;
}

.dot-wave span:nth-child(2) {
  animation-delay: -0.2s;
}

.dot-wave span:nth-child(3) {
  animation-delay: 0s;
}

@keyframes wave {
  0%,
  60%,
  100% {
    transform: scale(0);
  }

  30% {
    transform: scale(1);
  }
}
</style>
